import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, RotateCcw } from "lucide-react";
import { useLang } from "@/context";
import { T, MGA_RATE, formatPrice, linkTo } from "@/i18n";
import { ZONES } from "@/data/zones";
import { QUIZ } from "@/data/misc";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/motion";

function Calculator() {
  const { lang } = useLang();
  const t = T[lang].tools;
  const [price, setPrice] = useState(50000);
  const [withCompany, setWithCompany] = useState(true);
  const p = Number(price) || 0;
  const notary = p * 0.05;
  const legal = 1200;
  const company = withCompany ? 2000 : 0;
  const agency = p * 0.03;
  const total = p + notary + legal + company + agency;
  const rows = [
    [t.calcRows.price, p], [t.calcRows.notary, notary], [t.calcRows.legal, legal],
    ...(withCompany ? [[t.calcRows.company, company]] : []),
    [t.calcRows.agency, agency], [t.calcRows.total, total],
  ];
  return (
    <div data-testid="tool-calculator">
      <h2 className="font-serif text-2xl text-[#0E4D5C]">{t.calcTitle}</h2>
      <div className="mt-6 grid lg:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div>
            <label className="label-site">{t.calcPrice}</label>
            <input type="number" min="0" className="input-site" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="calc-price-input" />
          </div>
          <label className="flex items-center gap-3 text-sm cursor-pointer">
            <input type="checkbox" checked={withCompany} onChange={(e) => setWithCompany(e.target.checked)} className="h-4 w-4 accent-[#B5472B]" data-testid="calc-company-toggle" />
            {t.calcRows.company}
          </label>
          <p className="text-xs text-[#8A8F96] leading-relaxed">{t.calcNote}</p>
        </div>
        <div className="card-site p-6">
          <dl className="space-y-3">
            {rows.map(([label, val], i) => (
              <div key={label} className={`flex justify-between text-sm ${i === rows.length - 1 ? "border-t border-[#0E4D5C]/15 pt-3 font-semibold text-base" : ""}`}>
                <dt className="text-[#1F2328]/70">{label}</dt>
                <dd className={i === rows.length - 1 ? "font-serif text-xl text-[#B5472B]" : "font-medium"} data-testid={i === rows.length - 1 ? "calc-total" : undefined}>{formatPrice(Math.round(val), lang)}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}

function Converter() {
  const { lang } = useLang();
  const t = T[lang].tools;
  const [rate, setRate] = useState(MGA_RATE);
  const [eur, setEur] = useState("1000");
  const [mga, setMga] = useState(String(1000 * MGA_RATE));
  const fromEur = (v) => { setEur(v); setMga(v === "" ? "" : String(Math.round(Number(v) * rate))); };
  const fromMga = (v) => { setMga(v); setEur(v === "" ? "" : String(Math.round((Number(v) / rate) * 100) / 100)); };
  return (
    <div data-testid="tool-converter">
      <h2 className="font-serif text-2xl text-[#0E4D5C]">{t.convTitle}</h2>
      <div className="mt-6 grid sm:grid-cols-2 gap-5 max-w-2xl">
        <div>
          <label className="label-site">{t.convEur}</label>
          <input type="number" className="input-site" value={eur} onChange={(e) => fromEur(e.target.value)} data-testid="conv-eur-input" />
        </div>
        <div>
          <label className="label-site">{t.convMga}</label>
          <input type="number" className="input-site" value={mga} onChange={(e) => fromMga(e.target.value)} data-testid="conv-mga-input" />
        </div>
      </div>
      <div className="mt-4 max-w-2xl">
        <label className="label-site">1 € =</label>
        <input type="number" className="input-site !w-40" value={rate} onChange={(e) => { setRate(Number(e.target.value) || MGA_RATE); fromEur(eur); }} data-testid="conv-rate-input" />
        <p className="text-xs text-[#8A8F96] mt-3 leading-relaxed">{t.convNote}</p>
      </div>
    </div>
  );
}

function ZoneCompare() {
  const { lang } = useLang();
  const t = T[lang].tools;
  const [selected, setSelected] = useState(["nosy-be", "sainte-marie"]);
  const toggle = (slug) => {
    setSelected((s) => s.includes(slug) ? s.filter((x) => x !== slug) : s.length >= 3 ? s : [...s, slug]);
  };
  const zones = ZONES.filter((z) => selected.includes(z.slug));
  const rows = [
    [t.compareRows.profile, (z) => z[lang].profile],
    [t.compareRows.price, (z) => z[lang].price],
    [t.compareRows.climate, (z) => z[lang].climate],
    [t.compareRows.access, (z) => z[lang].access],
    [t.compareRows.best, (z) => z[lang].best],
  ];
  return (
    <div data-testid="tool-compare">
      <h2 className="font-serif text-2xl text-[#0E4D5C]">{t.compareTitle}</h2>
      <p className="mt-2 text-sm text-[#1F2328]/60">{t.comparePick}</p>
      <div className="mt-5 flex flex-wrap gap-2">
        {ZONES.map((z) => (
          <button key={z.slug} onClick={() => toggle(z.slug)} data-testid={`compare-zone-${z.slug}`}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${selected.includes(z.slug) ? "bg-[#B5472B] text-white" : "bg-[#0E4D5C]/5 text-[#0E4D5C] hover:bg-[#0E4D5C]/15"}`}>
            {z.name}
          </button>
        ))}
      </div>
      {zones.length > 0 && (
        <div className="mt-8 overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="border-b border-[#0E4D5C]/10">
                <th className="text-left py-3 pr-4"></th>
                {zones.map((z) => <th key={z.slug} className="text-left py-3 pr-4 font-serif text-lg text-[#0E4D5C]">{z.name}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#0E4D5C]/10">
              {rows.map(([label, fn]) => (
                <tr key={label}>
                  <td className="py-3 pr-4 text-[#8A8F96] font-medium align-top">{label}</td>
                  {zones.map((z) => <td key={z.slug} className="py-3 pr-4 text-[#1F2328]/80 align-top">{fn(z)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Quiz() {
  const { lang } = useLang();
  const t = T[lang].tools;
  const quiz = QUIZ[lang];
  const [step, setStep] = useState(-1);
  const [scores, setScores] = useState({});
  const pick = (score) => {
    const next = { ...scores };
    Object.entries(score).forEach(([k, v]) => { next[k] = (next[k] || 0) + v; });
    setScores(next);
    setStep(step + 1);
  };
  const restart = () => { setStep(-1); setScores({}); };
  const done = step >= quiz.questions.length;
  const winner = done ? Object.entries(scores).sort((a, b) => b[1] - a[1])[0]?.[0] : null;
  return (
    <div data-testid="tool-quiz">
      <h2 className="font-serif text-2xl text-[#0E4D5C]">{t.quizTitle}</h2>
      {step === -1 && (
        <div className="mt-6">
          <p className="text-[#1F2328]/70 max-w-xl">{t.quizDisclaimer}</p>
          <button onClick={() => setStep(0)} className="btn-primary mt-6" data-testid="quiz-start">{t.quizStart} <ArrowRight size={16} /></button>
        </div>
      )}
      {step >= 0 && !done && (
        <div className="mt-6 max-w-2xl">
          <div className="flex gap-1.5 mb-6">
            {quiz.questions.map((_, i) => (
              <span key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[#B5472B]" : "bg-[#0E4D5C]/10"}`} />
            ))}
          </div>
          <p className="font-serif text-xl text-[#1F2328]" data-testid="quiz-question">{quiz.questions[step].q}</p>
          <div className="mt-5 space-y-3">
            {quiz.questions[step].options.map((o, i) => (
              <button key={i} onClick={() => pick(o.score)} data-testid={`quiz-option-${i}`}
                className="w-full text-left card-site px-5 py-4 text-sm font-medium hover:!shadow-lg hover:border-[#B5472B]/40 transition-all">
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {done && winner && (
        <div className="mt-6 max-w-2xl rounded-3xl bg-[#0E4D5C] text-[#F6EFE4] p-8" data-testid="quiz-result">
          <p className="eyebrow !text-[#E8B64C]">{t.quizResult}</p>
          <h3 className="font-serif text-3xl mt-2">{quiz.results[winner].title}</h3>
          <p className="mt-4 text-[#F6EFE4]/85 leading-relaxed">{quiz.results[winner].text}</p>
          <p className="mt-4 text-xs text-[#F6EFE4]/55">{t.quizDisclaimer}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to={linkTo(lang, "contact")} className="btn-primary" data-testid="quiz-cta">{t.quizCta}</Link>
            <button onClick={restart} className="btn-outline !border-[#F6EFE4]/40 !text-[#F6EFE4] hover:!bg-[#F6EFE4] hover:!text-[#0E4D5C]" data-testid="quiz-restart"><RotateCcw size={15} /> {t.quizRestart}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Tools() {
  const { lang } = useLang();
  const t = T[lang].tools;
  const [tab, setTab] = useState("calc");
  const tabs = [["calc", t.calc], ["converter", t.converter], ["compare", t.compare], ["quiz", t.quiz]];
  return (
    <div className="container-site py-28 md:py-36" data-testid="tools-page">
      <SEO title={`${t.title} — ${T[lang].brand}`} description={t.sub} lang={lang} pathKey="tools" />
      <Reveal>
        <p className="eyebrow">{T[lang].nav.tools}</p>
        <h1 className="h2-serif mt-3">{t.title}</h1>
        <p className="mt-4 text-[#1F2328]/70 max-w-2xl">{t.sub}</p>
      </Reveal>
      <div className="mt-10 flex flex-wrap gap-2" data-testid="tools-tabs">
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tools-tab-${k}`}
            className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${tab === k ? "bg-[#B5472B] text-white" : "bg-[#0E4D5C]/5 text-[#0E4D5C] hover:bg-[#0E4D5C]/15"}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="mt-10 card-site p-6 md:p-10">
        {tab === "calc" && <Calculator />}
        {tab === "converter" && <Converter />}
        {tab === "compare" && <ZoneCompare />}
        {tab === "quiz" && <Quiz />}
      </div>
    </div>
  );
}
